import * as THREE from "three";
import * as BufferGeometryUtils from 'three/addons/utils/BufferGeometryUtils.js';
import { Text } from '@react-three/drei';
import { TubeCurve } from '../shape/Spline';
import React, { memo, useState, useEffect, useRef } from 'react';
import { drawIntersectionPoints,getContours } from './utils';

import { 
  calcCirclePoints, 
  calculateCurveCenter, 
  calculateCurveLength, 
  offsetCurve, 
  intersectionCurvePlane,
  calculateIntersectionPoints,
  lineSegmentsIntersect,
  findClosestToZeroY,
  calcIntersectionMedialCurves,
  findClosestPointOnPoints,
  remapCurve,
  alignSeamPoints,
  makeCurveClockwise,
  sortPoints,
  createLoftSurfaceFrom2Curves,
  createLoftSurfaceFromCurves,
  calculateTweenCurve,
  calcCentroid
} from '../shape/utils';
import { StepElement_VolumeElementPurpose } from "opencascade.js";
import { Point, Points } from "../shape/Point";
import { ControlCameraOutlined } from "@material-ui/icons";
import { scaleGeometry, gradualScaleGeometry } from "../shape/utils/geometry";


export const CircleCurve = ({ center, radius, segments, color }) => {
  const points = calcCirclePoints(center, radius, segments);

  const geometry = React.useMemo(() => {
    return new THREE.BufferGeometry().setFromPoints(points);
  }, [points]);

  const material = React.useMemo(() => {
    return new THREE.LineBasicMaterial({ color });
  }, [color]);

  return <line geometry={geometry} material={material} />;
};

const trimMedialPoints = (basePoints, medialTrimPoints) => {
  const areAllElementsVector3 = medialTrimPoints.every(
    (element) => element instanceof THREE.Vector3
  );
  if (areAllElementsVector3 === false) {
    medialTrimPoints = medialTrimPoints.map((point) => new THREE.Vector3(point.x, point.y, point.z));
  }
  // change control point from key points to spaced points on curve
  basePoints = new THREE.CatmullRomCurve3(basePoints).getSpacedPoints(100);

  const intersections = calcIntersectionMedialCurves(new THREE.CatmullRomCurve3(basePoints), new THREE.CatmullRomCurve3(medialTrimPoints));
  console.assert(intersections.length === 2, "intersections.length should be 2");
  const closestPoint0 = findClosestPointOnPoints(basePoints, intersections[0]); // z < 0
  const closestPoint1 = findClosestPointOnPoints(basePoints, intersections[1]); // z > 0

  var maxIdx = Math.max(closestPoint0.idx, closestPoint1.idx);
  var minIdx = Math.min(closestPoint0.idx, closestPoint1.idx);
  console.assert(maxIdx - minIdx > basePoints.length/2, "maxIdx - minIdx should be greater than basePoints.length/2")

  const normal = new THREE.Vector3(0, 0, 1);
  if (maxIdx - minIdx > basePoints.length/2) { // this is weak condition, could caouse error
    for (let i = maxIdx; i < basePoints.length; i++) {
      const plane = new THREE.Plane(normal, -basePoints[i].z);
      //var tempPoint = findClosestPointOnPoints(new THREE.CatmullRomCurve3(medialTrimPoints).getPoints(50), basePoints[i]);
      const intersectionPoint = intersectionCurvePlane(new THREE.CatmullRomCurve3(medialTrimPoints), plane, 50, close=false);
      console.assert(intersectionPoint.length === 1, "intersectionPoint.length should be 1");

      basePoints[i] = new THREE.Vector3(basePoints[i].x, intersectionPoint[0].y, basePoints[i].z);
    }
    for (let i = 0; i <= minIdx; i++) {
      const plane = new THREE.Plane(normal, -basePoints[i].z);
      //var tempPoint = findClosestPointOnPoints(new THREE.CatmullRomCurve3(medialTrimPoints).getPoints(100), basePoints[i]);
      const intersectionPoint = intersectionCurvePlane(new THREE.CatmullRomCurve3(medialTrimPoints), plane, 50, close=false);
      console.assert(intersectionPoint.length === 1, "intersectionPoint.length should be 1");

      basePoints[i] = new THREE.Vector3(basePoints[i].x, intersectionPoint[0].y, basePoints[i].z);
    }
  } else {
    for (let i = minIdx; i <= maxIdx; i++) {
      const plane = new THREE.Plane(normal, -basePoints[i].z);
      //var tempPoint = findClosestPointOnPoints(new THREE.CatmullRomCurve3(medialTrimPoints).getPoints(100), basePoints[i]);
      const intersectionPoint = intersectionCurvePlane(new THREE.CatmullRomCurve3(medialTrimPoints), plane, 50, close=false);
      console.assert(intersectionPoint.length === 1, "intersectionPoint.length should be 1");

      basePoints[i] = new THREE.Vector3(basePoints[i].x, intersectionPoint[0].y, basePoints[i].z);
    }
  }
  return basePoints;
}

const weftPoints2surface = (objects, warpPointsAll, angleDiv, segmentsWidth, segmentsHeight, color) => {
  const div = 30; 
  const divWarpPointsAll = [];
  const curveWarpAll = [];
  const loftSurfaces = [];
  
  for (let i = 0; i < angleDiv; i++) {
    const warpPoints = warpPointsAll.map((points) => points[i]);
    const curveWarp = new THREE.CatmullRomCurve3(warpPoints);
    curveWarpAll.push(curveWarp);
    const curveWarpPoints = curveWarp.getSpacedPoints(div);
    divWarpPointsAll.push(curveWarpPoints)
  }

  // weft curve points from warp curve points
  const divWeftPointsAll = [];
  for (let i = 0; i < div+1; i++) {
    const divWeftPoints = divWarpPointsAll.map((points) => points[i]);
    divWeftPointsAll.push(divWeftPoints);
  }

  ///////// Simple loft with three.js /////////////////////////////////////////////////
  if (objects.length !== 0) {
    //var geom = BufferGeometryUtils();
    for (let i = 0; i < divWeftPointsAll.length-1; i++) {
      const loftPoints0 = makeCurveClockwise(divWeftPointsAll[i]);
      const loftPoints1 = alignSeamPoints(loftPoints0, makeCurveClockwise(divWeftPointsAll[i+1]));
      const loftCurve0 = new THREE.CatmullRomCurve3(loftPoints0, true);
      const loftCurve1 = new THREE.CatmullRomCurve3(loftPoints1, true);

      const loftSurface = createLoftSurfaceFrom2Curves(loftCurve0, loftCurve1, segmentsWidth, segmentsHeight, color);
      loftSurfaces.push(loftSurface);
    }
    return loftSurfaces;
  }
}

function calculateEllipseRadius(circumference, ratio) {
  const defaultRy = 2 * circumference / (2 * Math.PI * (1 + ratio));
  const defaultRx = ratio * defaultRy;

  const epsilon = 0.07;
  const torelance = 0.25;
  let rx = defaultRx;
  let ry = rx / ratio;

  for (let i = 0; i < 10000; i++) {
    const c = 4 * (Math.PI * rx * ry + (rx - ry) ** 2) / (rx + ry);

    if (Math.abs(c - circumference) < torelance) {
      return { rx:rx, ry:ry };
    }
    if (c > circumference) {
      rx -= epsilon;
    } else {
      rx += epsilon;
    }
    ry = rx / ratio;
  }

  return { rx:defaultRx, ry:defaultRy };
}

/**
 * Show circumference value on the canvas
 */
export const CrossSectionText = memo(({
  oriMeshLeg,
  oriContourPointsAll,
  contourPointsAll,
  compression,

  color,
  colorHigh,
  colorSub
}) => {
  const objects = [];
  const objectsSub = [];
  var key = 0;

  const nCross = contourPointsAll.length;
  const legBboxMaxX = oriMeshLeg.geometry.boundingBox.max.x;
  for (let i=0; i<nCross; i++) {
    const offset = calcCentroid(contourPointsAll[i]).y;
    const textPosition = [legBboxMaxX + 20, offset, 0];

    const curveLength = new THREE.CatmullRomCurve3(contourPointsAll[i]).getLength();
    const oriCurveLength = new THREE.CatmullRomCurve3(oriContourPointsAll[i]).getLength();
    // calculate compressed radius
    const compressedRadius = (curveLength-oriCurveLength) / (2*Math.PI);

    //const oriCurveLength = 0;

    objectsSub.push(
      <Text
        key={key}
        //position={ (angle===6) ? [textPosition[0], textPosition[1], textPosition[2]-2*centroidBaseshape.x] : textPosition}
        //rotation={ (angle===6) ? [0, Math.PI/2, 0] : [0, 0, 0] }
        position={textPosition}
        rotation={[0, 0, 0]}
        color='white'
        fontSize={10}
        maxWidth={400} // option
        textAlign='left' // option
        anchorX='left' // option
        anchorY='middle' // option
      >
        {i}: {Math.round(oriCurveLength)}mm - {Math.round(compression[i-1]*10)/10}% {'->'} {Math.round(curveLength)}mm(diff:{Math.round(curveLength-oriCurveLength)}mm), radius:{Math.round(compressedRadius*10)/10}mm
      </Text>);
      key += 1;
  }

  return (
    ((objects.length > 0) || (objectsSub.length > 0)) ? 
      <group >
        {objects}
        {objectsSub}
      </group>
     : null
  );

});


export const CrossSectionAkText = memo(({
  activeStep,
  angle,
  stumpLength,
  interval,
  centroidBaseshape,
  points,
  bottomExtensionCurveRadius,
  bottomManualCircumference,
  secondBottomManualCircumference,
  manualCircumference,
  measuredCircumferenceLength,
  baseshapeCompression,
  compression,
}) => {
  const objectsSub = [];
  let key = 0;

  let nCrossSection = Math.floor(stumpLength / interval);

  const measuredCircumference = [];
  measuredCircumference.push(2*Math.PI*bottomExtensionCurveRadius);
  measuredCircumference.push(bottomManualCircumference)
  if (nCrossSection >= 2) measuredCircumference.push(secondBottomManualCircumference)
  if (nCrossSection >= 3) {
    for (let i = 3; i < nCrossSection+1; i++) {
      measuredCircumference.push(manualCircumference[i])
    }
  }
  measuredCircumference.push(measuredCircumferenceLength); // baseshape

  if (activeStep === 3) {
    // text fpr circumference length
    var textPosition = [0,0,0];
    for (let i = 0; i < nCrossSection+2; i++) {
      // convert array to vector3
      var pointsVector3 = points[i].map((point) => new THREE.Vector3(point.x, point.y, point.z));

      textPosition=[centroidBaseshape.x*3, -interval*(nCrossSection-i+1), 0]
      var circumference = new THREE.CatmullRomCurve3(pointsVector3).getLength();
      
      // calculate compressed radius
      var compressedRadius = (circumference - measuredCircumference[i]) / (2*Math.PI);

      var temp = '';
      var comp = 0;
      (i==nCrossSection+1) ? temp = ': Measured circumference' : temp = '';

      // Measured circumference
      objectsSub.push( <Text
        key={key}
        position={ (angle===6) ? [textPosition[0], textPosition[1], textPosition[2]-2*centroidBaseshape.x] : textPosition}
        rotation={ (angle===6) ? [0, Math.PI/2, 0] : [0, 0, 0] }
        color='white'
        fontSize={12}
        maxWidth={400} // option
        textAlign='left' // option
        anchorX='left' // option
        anchorY='middle' // option
      >
        {i}: {Math.round(measuredCircumference[i])} mm {temp}
      </Text>);
      key += 1;

      if (i===0) continue;

      // Compressed circumference
      (i==nCrossSection+1) ? temp = ': Compressed circumference' : temp = '';
      (i==nCrossSection+1) ? comp = baseshapeCompression : comp = compression[i];
      textPosition = [textPosition[0], textPosition[1]-15, textPosition[2]];
      objectsSub.push( <Text
        key={key}
        position={ (angle===6) ? [textPosition[0], textPosition[1], textPosition[2]-2*centroidBaseshape.x] : textPosition}
        rotation={ (angle===6) ? [0, Math.PI/2, 0] : [0, 0, 0] }
        color='white'
        fontSize={12}
        maxWidth={400} // option
        textAlign='left' // option
        anchorX='left' // option
        anchorY='middle' // option
      >
        {}    {Math.round(circumference)} mm ({comp} %) {temp}
      </Text>);
      key += 1;

      // Compressed radius
      (i==nCrossSection+1) ? temp = ': Compressed radius' : temp = '';
      textPosition = [textPosition[0], textPosition[1]-15, textPosition[2]];
      objectsSub.push( <Text
        key={key}
        position={ (angle===6) ? [textPosition[0], textPosition[1], textPosition[2]-2*centroidBaseshape.x] : textPosition}
        rotation={ (angle===6) ? [0, Math.PI/2, 0] : [0, 0, 0] }
        color='white'
        fontSize={12}
        maxWidth={400} // option
        textAlign='left' // option
        anchorX='left' // option
        anchorY='middle' // option
      >
        {}    {Math.round(compressedRadius*10)/10} mm {temp}
      </Text>);
      key += 1;
    }
  }

  return (
    (objectsSub.length > 0) ? 
      <group >
        {objectsSub}
      </group>
     : null
  );

});

export const CrossSectionAkScanText = memo(({
  activeStep,
  angle,
  interval,
  centroidBaseshape,
  points,
  oriPoints,
  baseshapeCompression,
  compression,
}) => {
  const objectsSub = [];
  let key = 0;

  const nCrossSection = points.length

  if (activeStep === 3) {
    // text fpr circumference length
    let textPosition = [0,0,0];
    for (let i = 0; i < nCrossSection; i++) {
      // convert array to vector3
      const pointsVector3 = points[i].map((point) => new THREE.Vector3(point.x, point.y, point.z));

      textPosition=[centroidBaseshape.x*3, -interval*(nCrossSection-i-1), 0]
      const circumference = new THREE.CatmullRomCurve3(pointsVector3).getLength();
      const oriCircumference = new THREE.CatmullRomCurve3(oriPoints[i]).getLength();
      
      // calculate compressed radius
      let compressedRadius = (circumference - oriCircumference) / (2*Math.PI);

      var temp = '';
      var comp = 0;
      (i==nCrossSection-1) ? temp = ': Scanned or measured circumference' : temp = '';

      // Scan circumference
      objectsSub.push( <Text
        key={key}
        position={ (angle===6) ? [textPosition[0], textPosition[1], textPosition[2]-2*centroidBaseshape.x] : textPosition}
        rotation={ (angle===6) ? [0, Math.PI/2, 0] : [0, 0, 0] }
        color='white'
        fontSize={12}
        maxWidth={400} // option
        textAlign='left' // option
        anchorX='left' // option
        anchorY='middle' // option
      >
        {i}: {Math.round(oriCircumference)} mm {temp}
      </Text>);
      key += 1;

      // Compressed circumference
      (i==nCrossSection-1) ? temp = ': Compressed circumference' : temp = '';
      (i==nCrossSection-1) ? comp = baseshapeCompression : comp = compression[i];
      textPosition = [textPosition[0], textPosition[1]-15, textPosition[2]];
      objectsSub.push( <Text
        key={key}
        position={ (angle===6) ? [textPosition[0], textPosition[1], textPosition[2]-2*centroidBaseshape.x] : textPosition}
        rotation={ (angle===6) ? [0, Math.PI/2, 0] : [0, 0, 0] }
        color='white'
        fontSize={12}
        maxWidth={400} // option
        textAlign='left' // option
        anchorX='left' // option
        anchorY='middle' // option
      >
        {}    {Math.round(circumference)} mm ({comp} %) {temp}
      </Text>);
      key += 1;

      // Compressed radius
      (i==nCrossSection-1) ? temp = ': Compressed radius' : temp = '';
      textPosition = [textPosition[0], textPosition[1]-15, textPosition[2]];
      objectsSub.push( <Text
        key={key}
        position={ (angle===6) ? [textPosition[0], textPosition[1], textPosition[2]-2*centroidBaseshape.x] : textPosition}
        rotation={ (angle===6) ? [0, Math.PI/2, 0] : [0, 0, 0] }
        color='white'
        fontSize={12}
        maxWidth={400} // option
        textAlign='left' // option
        anchorX='left' // option
        anchorY='middle' // option
      >
        {}    {Math.round(compressedRadius*10)/10} mm {temp}
      </Text>);
      key += 1;
    }
  }

  return (
    (objectsSub.length > 0) ? 
      <group >
        {objectsSub}
      </group>
     : null
  );
});



/**
 * Calculate cross sections for BK
 */
export const CrossSectionsBk = memo(({
  oriMeshLeg,
  oriMeshSocket,
  compressionWhole,
  compression,
  no,
  interval,
  smoothness,
  bottomExtension,
  step,
  callback0,
  callback1,
  callback2,
  callback3,
  callback4,

  color,
  colorHigh,
  colorSub
}) => {
  const [objects, setObjects] = useState([]); // cross sections
  const [objectsSub, setObjectsSub] = useState([]); // cross sections
  
  const contourPointsAll = [];
  const oriContourPointsAll = [];

  var key = 0;
  var ref = useRef();
  const nBasePoints = 50;
  const segmentsWidth = 40;
  const segmentsHeight = 100;

  useEffect(() => {
    console.log("CrossSectionsBk Local");
    if (!oriMeshLeg) return;

    objects.length = 0; // clear array
    objectsSub.length = 0; // clear array

    // calc stump length from bounding box of the leg mesh
    const meshLeg = oriMeshLeg.clone();
    meshLeg.geometry.computeBoundingBox();
    const stumpLength = meshLeg.geometry.boundingBox.min.y;
    const bbox = meshLeg.geometry.boundingBox;
    let meshSocket = oriMeshSocket.clone();
    meshSocket.geometry = oriMeshSocket.geometry.clone();

    const nCross = Math.ceil((bbox.max.y-bbox.min.y) / interval) - 2
    const offsets = [];

    // get contour points
    for (let i=0; i<nCross; i++) {
      const offset = bbox.min.y+(i+1)*interval;
      offsets.push(offset);

      var plane = new THREE.Plane(new THREE.Vector3(0,1,0), -offset);
      var vertices = drawIntersectionPoints(meshSocket, plane, offset, false)
      
      if (vertices.length == 0) continue

      var contourPoints = sortPoints(vertices);
      const seamPoint = [new THREE.Vector3(0, 0, 1000)];
      contourPoints = alignSeamPoints(seamPoint, contourPoints);
      contourPoints = new THREE.CatmullRomCurve3(contourPoints, true).getSpacedPoints(nBasePoints);
      contourPoints = sortPoints(contourPoints); // make sure the contour points are sorted
      contourPoints = new THREE.CatmullRomCurve3(contourPoints, true).getSpacedPoints(nBasePoints);
      contourPoints = makeCurveClockwise(contourPoints);

      // compression
      const oriCurveLength = new THREE.CatmullRomCurve3(contourPoints).getLength();
      const oriContourPoints = contourPoints.map((point) => {
        return point.clone();
      });
      // Smooth value is 1 to 10. Smooth func reduce the number of points of the curve by 1/smoothness. 
      // Then return to the original number of points by using CatmullRomCurve3
      contourPoints = new THREE.CatmullRomCurve3(contourPoints, false).getSpacedPoints(Math.floor(contourPoints.length/smoothness[i]));
      
      // remove last point because the last point is the same as the first point of the curve due to the closed curve
      contourPoints.pop();
      contourPoints = new THREE.CatmullRomCurve3(contourPoints, true).getSpacedPoints(nBasePoints);

      const curveLength = new THREE.CatmullRomCurve3(contourPoints).getLength();
      const compressedCircumference = curveLength * compression[i]/100;
      const offsetCircumference = compressedCircumference / curveLength * oriCurveLength / curveLength;
      
      contourPoints = offsetCurve(new THREE.CatmullRomCurve3(contourPoints), offsetCircumference, calcCentroid(contourPoints));

      // show length of the curve
      var circumference = new THREE.CatmullRomCurve3(contourPoints).getLength();

      // bottom extension
      if (i == 0) {
        const bottomExtensionPoints = [];
        const curve = new THREE.CatmullRomCurve3(contourPoints);
        const center = calculateCurveCenter(curve);
        const offsetContourPoints = offsetCurve(curve, 0.5);
        for (let j=0; j<offsetContourPoints.length; j++) {
          const point = offsetContourPoints[j];
          const offset = stumpLength - center.y - bottomExtension*1;
          bottomExtensionPoints.push(new THREE.Vector3(point.x, point.y+offset, point.z));
        }
        contourPointsAll.push(bottomExtensionPoints);
        oriContourPointsAll.push(bottomExtensionPoints);

        callback3(bottomExtensionPoints);

        contourPoints = contourPoints.map((point) => {
          return new THREE.Vector3(point.x, point.y-bottomExtension, point.z);
        });
      }
      
      contourPointsAll.push(contourPoints);
      oriContourPointsAll.push(oriContourPoints);

      // objectsSub.push(<TubeCurve 
      //   key={key} 
      //   vpoints={contourPoints} 
      //   radius={0.5}
      //   color={colorHigh}
      //   />
      // );
      // key += 1;
    }

    callback0(oriContourPointsAll, contourPointsAll);

    // get meidan points
    const medianPointsAll = [];
    for (let angle = 0; angle < 360; angle += 90) {
      const radians = THREE.MathUtils.degToRad(angle);
      const normal = new THREE.Vector3(Math.cos(radians), 0, Math.sin(radians));
      console.log(angle)
      console.log(normal)
      
      const medianPoints = [];
      for (let i=0; i<contourPointsAll.length; i++) {
        const curve = new THREE.CatmullRomCurve3(contourPointsAll[i]);
        const center = calculateCurveCenter(curve);
        const plane = new THREE.Plane(normal, -normal.dot(center));

        const intersectionPoints = intersectionCurvePlane(curve, plane, 300);
        if (intersectionPoints.length == 0) continue;

        var refPoint = normal.clone().multiplyScalar(50);
        // rotation matrix to rotate around y axis to make the normal vector of the plane to be (0, 0, 1)
        var rotationMatrix = new THREE.Matrix4().makeRotationAxis(new THREE.Vector3(0, 1, 0), Math.PI / 2);
        // apply rotation
        refPoint.applyMatrix4(rotationMatrix);
        refPoint = refPoint.add(center);

        const closestPoint = findClosestPointOnPoints(intersectionPoints, refPoint);
        
        medianPoints.push(closestPoint.point);
      }
      medianPointsAll.push(medianPoints);
    }

    callback1(medianPointsAll);

    if (step == 0) {
      // reset loftsurface geometry as empty geometry
      callback2(null);
    }

    if (step == 1) {
      // // This if for loft surface from cross section
      if (false) {
        const loftSurface = createLoftSurfaceFromCurves(contourPointsAll, segmentsWidth, segmentsHeight, color, true);
        loftSurface.geometry = BufferGeometryUtils.mergeVertices(loftSurface.geometry);
        //loftSurface.geometry.computeVertexNormals();
        callback2(loftSurface);
      } else {
        // meshSocket.geometry = scaleGeometry(meshSocket.geometry, 1.1, 1, 1.1)
        meshSocket.geometry = gradualScaleGeometry(meshSocket.geometry, offsets, compression)

        // bottom extention
        for (let j=0; j<meshSocket.geometry.attributes.position.array.length; j+=3) {
          if (meshSocket.geometry.attributes.position.array[j+1] < offsets[2]) {
            meshSocket.geometry.attributes.position.array[j+1] -= bottomExtension;
          }
        }

        callback2(meshSocket);
      }

      callback4(2);

      // objects.push(<primitive key={key} object={loftSurface} />);
      // key += 1;
    }

     setObjects(objects);
     setObjectsSub(objectsSub);
  }, [oriMeshLeg,
    compressionWhole,
    compression,
    no,
    interval,
    smoothness,
    bottomExtension,
    step
  ]);



  return null;
});




/**
 * Calculate cross sections BK and scale the mesh
 */
export const CrossSectionsBkMeshBased = memo(({
  oriMeshLeg,
  oriMeshSocket,
  compressionWhole,
  compression,
  no,
  interval,
  smoothness,
  bottomExtension,
  step,
  callback0,
  callback1,
  callback2,
  callback3,
  callback4,

  color,
  colorHigh,
  colorSub
}) => {
  const [objects, setObjects] = useState([]); // cross sections
  const [objectsSub, setObjectsSub] = useState([]); // cross sections
  
  const contourPointsAll = [];
  const oriContourPointsAll = [];

  var key = 0;
  var ref = useRef();
  const nBasePoints = 100 //50;
  const segmentsWidth = 40;
  const segmentsHeight = 100;

  useEffect(() => {
    console.log("CrossSectionsBk Mesh Scale");
    if (!oriMeshLeg) return;

    objects.length = 0; // clear array
    objectsSub.length = 0; // clear array

    // calc stump length from bounding box of the leg mesh
    const meshLeg = oriMeshLeg.clone();
    meshLeg.geometry.computeBoundingBox();
    const stumpLength = meshLeg.geometry.boundingBox.min.y;
    const bbox = meshLeg.geometry.boundingBox;
    let meshSocket = oriMeshSocket.clone();

    const nCross = Math.ceil((bbox.max.y-bbox.min.y) / interval) - 2

    // get contour points
    for (let i=0; i<nCross; i++) {
      const offset = bbox.min.y+(i+1)*interval;

      var plane = new THREE.Plane(new THREE.Vector3(0,1,0), -offset);
      var vertices = drawIntersectionPoints(meshSocket, plane, offset, false)
      
      if (vertices.length == 0) continue

      var contourPoints = sortPoints(vertices);
      const seamPoint = [new THREE.Vector3(0, 0, 1000)];
      contourPoints = alignSeamPoints(seamPoint, contourPoints);
      contourPoints = new THREE.CatmullRomCurve3(contourPoints, true).getSpacedPoints(nBasePoints);
      contourPoints = sortPoints(contourPoints); // make sure the contour points are sorted
      contourPoints = new THREE.CatmullRomCurve3(contourPoints, true).getSpacedPoints(nBasePoints);
      contourPoints = makeCurveClockwise(contourPoints);

      // compression
      const oriCurveLength = new THREE.CatmullRomCurve3(contourPoints).getLength();
      const oriContourPoints = contourPoints.map((point) => {
        return point.clone();
      });
      // Smooth value is 1 to 10. Smooth func reduce the number of points of the curve by 1/smoothness. 
      // Then return to the original number of points by using CatmullRomCurve3
      contourPoints = new THREE.CatmullRomCurve3(contourPoints, false).getSpacedPoints(Math.floor(contourPoints.length/smoothness[i]));
      
      // remove last point because the last point is the same as the first point of the curve due to the closed curve
      contourPoints.pop();
      contourPoints = new THREE.CatmullRomCurve3(contourPoints, true).getSpacedPoints(nBasePoints);

      const curveLength = new THREE.CatmullRomCurve3(contourPoints).getLength();
      const compressedCircumference = curveLength * compression[i]/100;
      const offsetCircumference = compressedCircumference / curveLength * oriCurveLength / curveLength;
      
      contourPoints = offsetCurve(new THREE.CatmullRomCurve3(contourPoints), offsetCircumference, calcCentroid(contourPoints));

      // show length of the curve
      var circumference = new THREE.CatmullRomCurve3(contourPoints).getLength();

      // bottom extension
      if (i == 0) {
        const bottomExtensionPoints = [];
        const curve = new THREE.CatmullRomCurve3(contourPoints);
        const center = calculateCurveCenter(curve);
        const offsetContourPoints = offsetCurve(curve, 0.5);
        for (let j=0; j<offsetContourPoints.length; j++) {
          const point = offsetContourPoints[j];
          const offset = stumpLength - center.y - bottomExtension*1;
          bottomExtensionPoints.push(new THREE.Vector3(point.x, point.y+offset, point.z));
        }
        contourPointsAll.push(bottomExtensionPoints);
        oriContourPointsAll.push(bottomExtensionPoints);

        callback3(bottomExtensionPoints);

        contourPoints = contourPoints.map((point) => {
          return new THREE.Vector3(point.x, point.y-bottomExtension, point.z);
        });
      }
      
      contourPointsAll.push(contourPoints);
      oriContourPointsAll.push(oriContourPoints);

      // objectsSub.push(<TubeCurve 
      //   key={key} 
      //   vpoints={contourPoints} 
      //   radius={0.5}
      //   color={colorHigh}
      //   />
      // );
      // key += 1;
    }

    

    if (step == 0) {
      // reset loftsurface geometry as empty geometry
      callback2(null);
    }

    if (step == 1) {
      meshSocket.geometry = scaleGeometry(meshSocket.geometry, 1.1, 1, 1.1)

      callback2(meshSocket);
      callback4(2);
    }

     setObjects(objects);
     setObjectsSub(objectsSub);
  }, [oriMeshLeg,
    compressionWhole,
    compression,
    no,
    interval,
    smoothness,
    bottomExtension,
    step
  ]);

  return null;
});






/**
 * Calculate cross sections for AK
 */
export const CrossSectionsAk = memo(({
  oc,
  activeStep,
  side,
  stumpLength, 
  basePoints,
  bottomExtension,
  measuredCircumferenceLength,
  circumferenceLength, 
  centroidBaseshape,
  baseshapeCompression,
  bottomManualCircumference,
  bottomRatioXY,
  secondBottomManualCircumference,
  secondBottomRatioXY,
  bottomExtensionCurveRadius,
  initialAdductionAngle,
  initialFlexionAngle,
  line_no,
  manualCircumference,
  tweenFactor,
  compression,
  intervalAk,
  //medialTrimPoints,
  p0Y,
  p0Z,
  p1Y,
  p1Z,
  p2Y,
  p2Z,
  p3Y,
  p3Z,
  color, 
  colorHigh,
  colorSub,
  callback,
  callback2,
  segments=36
}) => {
  var objects = []; // cross sections
  var objectsSub = []; // text, warp curve, etc
  var objectsSurface = []; // surface
  var key = 0;
  const segmentsWidth = 100;
  const segmentsHeight = 10;

  const refGroup = useRef();

  var radius = circumferenceLength / (2 * Math.PI);
  const interval = intervalAk;
  var nCrossSection = Math.floor(stumpLength / interval);
  console.log(nCrossSection)
  var center = new THREE.Vector3(0, 0, 0);

  if (activeStep !== 3) {
    colorHigh = color;
  }

  const medialTrimPoints = [
    new THREE.Vector3(0, p0Y, p0Z),
    new THREE.Vector3(0, p1Y, p1Z),
    new THREE.Vector3(0, p2Y, p2Z),
    new THREE.Vector3(0, p3Y, p3Z),
  ]

  const measuredCircumference = [];
  measuredCircumference.push(2*Math.PI*bottomExtensionCurveRadius);
  basePoints = remapCurve(new THREE.CatmullRomCurve3(basePoints), 2);
  //basePoints = calculateIntersectionPoints(new THREE.CatmullRomCurve3(basePoints), 10, close=true);

  // bottom extension curve /////////////////////////////////////////////////
  center = new THREE.Vector3(0, -stumpLength-bottomExtension ,0) // rotation center and object center is defined as the below centroidBaseshape
  var bottomExtensionPoints = calcCirclePoints(center, bottomExtensionCurveRadius, segments);
  
  bottomExtensionPoints = bottomExtensionPoints.map((point) => {
    var newPoint = new THREE.Vector3(point.x, point.y, point.z);
    newPoint.applyAxisAngle(new THREE.Vector3(1, 0, 0), -initialFlexionAngle*Math.PI/180);
    newPoint.applyAxisAngle(new THREE.Vector3(0, 0, 1), -initialAdductionAngle*Math.PI/180);
    newPoint = newPoint.add(centroidBaseshape);
    return newPoint;
  });
  
  objects.push(<TubeCurve 
    key={key} 
    vpoints={bottomExtensionPoints} 
    radius={1} 
    color={ (line_no===0) ? colorHigh : color }
    />)
  key += 1;

  ////////////////// bottom curve ///////////////////////
  center = new THREE.Vector3(0, -stumpLength ,0); // rotation center and object center is defined as the below centroidBaseshape

  // compression
  measuredCircumference.push(bottomManualCircumference)
  const compressedBottomManualCircumference = bottomManualCircumference * compression[1]/100;

  var temp = calculateEllipseRadius(compressedBottomManualCircumference, bottomRatioXY);
  var rx = temp.rx;
  var ry = temp.ry;

  var bottomCircleCurvePoints = new THREE.EllipseCurve(center.x, center.z, rx, ry, 0, 2*Math.PI, true).getPoints(100);
  bottomCircleCurvePoints = bottomCircleCurvePoints.map((point) => new THREE.Vector3(point.x, center.y, point.y));
  
  // flexion and adduction
  bottomCircleCurvePoints = bottomCircleCurvePoints.map((point) => {
    var newPoint = new THREE.Vector3(point.x, point.y, point.z);
    newPoint.applyAxisAngle(new THREE.Vector3(1, 0, 0), -initialFlexionAngle*Math.PI/180);
    newPoint.applyAxisAngle(new THREE.Vector3(0, 0, 1), -initialAdductionAngle*Math.PI/180);
    newPoint = newPoint.add(centroidBaseshape);
    return newPoint;
  });
  
  objects.push(<TubeCurve 
    key={key} 
    vpoints={bottomCircleCurvePoints} 
    radius={1} 
    color={ (line_no===1) ? colorHigh : color }
     />
  );
  key += 1;

  //////////////////////// elpise curves ///////////////////////
  if (nCrossSection >= 4) {
    for (let i = 2; i < nCrossSection-1; i++) {
      center = new THREE.Vector3(0, -interval*(nCrossSection-i+1) ,0);
      
      if (i == 2) {
        measuredCircumference.push(secondBottomManualCircumference)
      } else {
        measuredCircumference.push(manualCircumference[i])
      }
      const compressedElipseManualCircumference = (i == 2) ? secondBottomManualCircumference * compression[i]/100 : manualCircumference[i] * compression[i]/100;
      const temp = (i == 2) ? calculateEllipseRadius(compressedElipseManualCircumference, secondBottomRatioXY) : calculateEllipseRadius(compressedElipseManualCircumference, tweenFactor[i]);
      
      const rx = temp.rx;
      const ry = temp.ry;
      
      var elipseCurvePoints = new THREE.EllipseCurve(center.x, center.z, rx, ry, 0, 2*Math.PI, true).getPoints(100);
      elipseCurvePoints = elipseCurvePoints.map((point) => new THREE.Vector3(point.x, center.y, point.y));
      
      elipseCurvePoints = elipseCurvePoints.map((point) => {
        var newPoint = new THREE.Vector3(point.x, point.y, point.z);
        newPoint.applyAxisAngle(new THREE.Vector3(1, 0, 0), -initialFlexionAngle*Math.PI/180);
        newPoint.applyAxisAngle(new THREE.Vector3(0, 0, 1), -initialAdductionAngle*Math.PI/180);
        newPoint = newPoint.add(centroidBaseshape);
        return newPoint;
      });
      
      objects.push(<TubeCurve 
        key={key} 
        vpoints={elipseCurvePoints} 
        radius={1} 
        color={ (line_no===i) ? colorHigh : color } 
        //rotation={new THREE.Vector3(-initialFlexionAngle*Math.PI/180, 0, -initialAdductionAngle*Math.PI/180)}
        //enter={centroidBaseshape} 
        />)
      key += 1;
    }
  }

  // //////////////////////// second bottom curve ///////////////////////
  // if (nCrossSection >= 2) {
  //   //center = new THREE.Vector3(centroidBaseshape.x, -interval*(nCrossSection-1) ,centroidBaseshape.z);
  //   //center = new THREE.Vector3(0, -interval*(nCrossSection-1) ,0); // rotation center and object center is defined as the below centroidBaseshape
  //   center = new THREE.Vector3(0, -interval*(nCrossSection-2) ,0);
    
  //   measuredCircumference.push(520)
  //   const compressedSecondBottomManualCircumference = 520 * compression[2]/100;

  //   var temp = calculateEllipseRadius(compressedSecondBottomManualCircumference, 1.1);
  //   var rx = temp.rx;
  //   var ry = temp.ry;
    
  //   var secondBottomCircleCurvePoints = new THREE.EllipseCurve(center.x, center.z, rx, ry, 0, 2*Math.PI, true).getPoints(100);
  //   secondBottomCircleCurvePoints = secondBottomCircleCurvePoints.map((point) => new THREE.Vector3(point.x, center.y, point.y));
    
  //   secondBottomCircleCurvePoints = secondBottomCircleCurvePoints.map((point) => {
  //     var newPoint = new THREE.Vector3(point.x, point.y, point.z);
  //     newPoint.applyAxisAngle(new THREE.Vector3(1, 0, 0), -initialFlexionAngle*Math.PI/180);
  //     newPoint.applyAxisAngle(new THREE.Vector3(0, 0, 1), -initialAdductionAngle*Math.PI/180);
  //     newPoint = newPoint.add(centroidBaseshape);
  //     return newPoint;
  //   });
    
  //   objects.push(<TubeCurve 
  //     key={key} 
  //     vpoints={secondBottomCircleCurvePoints} 
  //     radius={1} 
  //     color={ (line_no===2) ? colorHigh : color } 
  //     //rotation={new THREE.Vector3(-initialFlexionAngle*Math.PI/180, 0, -initialAdductionAngle*Math.PI/180)}
  //     //enter={centroidBaseshape} 
  //     />)
  //   key += 1;
  // }
  
  
  ////////////////// Tween curve ///////////////////////
  if (nCrossSection >= 3) {
    for (let i = nCrossSection-1; i < nCrossSection+1; i++) {
      // curve2 for tween, calculate offset baseshapecurve for tween curve
      const curve1Points = offsetCurve(new THREE.CatmullRomCurve3(basePoints), 1.0, centroidBaseshape);
      
      // curve1 for tween, circle: 
      const curve2Points = calcCirclePoints(centroidBaseshape, radius, segments);
      
      var angleStep = 10;
      const intersectionPoints1 = calculateIntersectionPoints(new THREE.CatmullRomCurve3(curve1Points), angleStep, close=true); // base curve for tween
      let intersectionPoints2 = calculateIntersectionPoints(new THREE.CatmullRomCurve3(curve2Points), angleStep, close=true); // base curve for tween
      
      // align inner line as straight line. Because after tween curve, the inner line is not straight line.
      intersectionPoints2 = intersectionPoints2.map((point) => {
        if (point.x < 5) {
          point.x = 5;
        }
        return point;
      });

      // // Nagaraj.C_AKTS
      // if (i == 3) {
      //   intersectionPoints1[3].add(new THREE.Vector3(0, 0, 1.5))
      //   intersectionPoints1[4].add(new THREE.Vector3(0, 0, 5))
      //   intersectionPoints1[5].add(new THREE.Vector3(0, 0, 10))
      //   intersectionPoints1[6].add(new THREE.Vector3(0, 0, 14))
      //   intersectionPoints1[7].add(new THREE.Vector3(0, 0, 12))
      //   intersectionPoints1[8].add(new THREE.Vector3(2, 0, -2))
      //   intersectionPoints1[9].add(new THREE.Vector3(1, 0, -2))
      //   intersectionPoints1[10].add(new THREE.Vector3(-3, 0, 0))
      //   intersectionPoints1[11].add(new THREE.Vector3(-4, 0, 0))
        
      //   intersectionPoints1[12].add(new THREE.Vector3(0, 0, -2))
      //   intersectionPoints1[13].add(new THREE.Vector3(0, 0, -6))
      //   intersectionPoints1[14].add(new THREE.Vector3(0, 0, -6))
      //   intersectionPoints1[15].add(new THREE.Vector3(0, 0, -4))
      // }

      // if (i == 4) {
      //   intersectionPoints1[3].add(new THREE.Vector3(0, 0, 1.5))
      //   intersectionPoints1[4].add(new THREE.Vector3(0, 0, 5))
      //   intersectionPoints1[5].add(new THREE.Vector3(0, 0, 10))
      //   intersectionPoints1[6].add(new THREE.Vector3(0, 0, 14))
      //   intersectionPoints1[7].add(new THREE.Vector3(0, 0, 12))
      //   intersectionPoints1[8].add(new THREE.Vector3(3, 0, -2))
      //   intersectionPoints1[9].add(new THREE.Vector3(2, 0, -2))
      //   intersectionPoints1[10].add(new THREE.Vector3(0, 0, 0))
      //   intersectionPoints1[11].add(new THREE.Vector3(0, 0, 0))
        
      //   intersectionPoints1[12].add(new THREE.Vector3(0, 0, -2))
      //   intersectionPoints1[13].add(new THREE.Vector3(0, 0, -6))
      //   intersectionPoints1[14].add(new THREE.Vector3(0, 0, -6))
      //   intersectionPoints1[15].add(new THREE.Vector3(0, 0, -4))
      // }

      // if (i == 4) {
      //   intersectionPoints1[3].add(new THREE.Vector3(0, 0, 1.5))
      //   intersectionPoints1[4].add(new THREE.Vector3(0, 0, 5))
      //   intersectionPoints1[5].add(new THREE.Vector3(0, 0, 10))
      //   intersectionPoints1[6].add(new THREE.Vector3(0, 0, 14))
      //   intersectionPoints1[7].add(new THREE.Vector3(0, 0, 12))
      //   intersectionPoints1[8].add(new THREE.Vector3(0, 0, 5))
      //   intersectionPoints1[12].add(new THREE.Vector3(0, 0, -4))
      //   intersectionPoints1[13].add(new THREE.Vector3(0, 0, -6))
      //   intersectionPoints1[14].add(new THREE.Vector3(0, 0, -6))
      //   intersectionPoints1[15].add(new THREE.Vector3(0, 0, -4))
      // }

      // if (i == 5) {
      //   intersectionPoints1[4].add(new THREE.Vector3(0, 0, 3))
      //   intersectionPoints1[5].add(new THREE.Vector3(0, 0, 5))
      //   intersectionPoints1[6].add(new THREE.Vector3(0, 0, 5))
      //   intersectionPoints1[7].add(new THREE.Vector3(0, 0, 1))
      //   intersectionPoints1[8].add(new THREE.Vector3(0, 0, 1))
      // }


      const t = (i==2) ? secondBottomRatioXY : tweenFactor[i];
      var tweenCurvePoints = calculateTweenCurve(intersectionPoints1, intersectionPoints2, t); // tween curve

      // show tween curve
      // objects.push(<TubeCurve
      //   key={key}
      //   vpoints={tweenCurvePoints}
      //   radius={0.5}
      //   color={colorSub}
      // />)

      if (i == 2) {
        measuredCircumference.push(secondBottomManualCircumference)
      } else {
        measuredCircumference.push(manualCircumference[i])
      }
      const compressedManualCircumference = (i == 2) ? secondBottomManualCircumference * compression[i]/100 : manualCircumference[i] * compression[i]/100;

      var offset = compressedManualCircumference / calculateCurveLength(new THREE.CatmullRomCurve3(tweenCurvePoints));
      //var offset = manualCircumference[i] / calculateCurveLength(new THREE.CatmullRomCurve3(tweenCurvePoints));
      tweenCurvePoints = offsetCurve(new THREE.CatmullRomCurve3(tweenCurvePoints), offset, centroidBaseshape);
      
      center = new THREE.Vector3(0, -interval*(nCrossSection-i+1) ,0);

      tweenCurvePoints = tweenCurvePoints.map((point) => new THREE.Vector3(point.x-centroidBaseshape.x, point.y-centroidBaseshape.y+center.y, point.z-centroidBaseshape.z));

      // flexion and adduction
      tweenCurvePoints = tweenCurvePoints.map((point) => {
        var newPoint = new THREE.Vector3(point.x, point.y, point.z);
        newPoint.applyAxisAngle(new THREE.Vector3(1, 0, 0), -initialFlexionAngle*Math.PI/180);
        newPoint.applyAxisAngle(new THREE.Vector3(0, 0, 1), -initialAdductionAngle*Math.PI/180);
        newPoint = newPoint.add(centroidBaseshape);
        return newPoint;
      });

      // tweenCurvePoints = new THREE.CatmullRomCurve3(tweenCurvePoints).getSpacedPoints(36);
      // tweenCurvePoints = new THREE.CatmullRomCurve3(tweenCurvePoints).getSpacedPoints(37);
      tweenCurvePoints = calculateIntersectionPoints(new THREE.CatmullRomCurve3(tweenCurvePoints), angleStep, close=true);

      objects.push(<TubeCurve
        key={key} 
        vpoints={tweenCurvePoints} 
        radius={1} 
        color={ (line_no===i) ? colorHigh : color }
        //rotation={new THREE.Vector3(-initialFlexionAngle*Math.PI/180, 0, -initialAdductionAngle*Math.PI/180)}
        //center={centroidBaseshape}
      />)
      key += 1;
    }
  } else if (nCrossSection == 2) {
    // curve2 for tween, calculate offset baseshapecurve for tween curve
    const curve1Points = offsetCurve(new THREE.CatmullRomCurve3(basePoints), 1.0, centroidBaseshape);
    
    // curve1 for tween, circle: 
    const curve2Points = calcCirclePoints(centroidBaseshape, radius, segments);
    
    var angleStep = 10;
    const intersectionPoints1 = calculateIntersectionPoints(new THREE.CatmullRomCurve3(curve1Points), angleStep, close=true); // base curve for tween
    let intersectionPoints2 = calculateIntersectionPoints(new THREE.CatmullRomCurve3(curve2Points), angleStep, close=true); // base curve for tween
    
    // align inner line as straight line. Because after tween curve, the inner line is not straight line.
    intersectionPoints2 = intersectionPoints2.map((point) => {
      if (point.x < 5) {
        point.x = 5;
      }
      return point;
    });

    console.log(tweenFactor)
    const t = secondBottomRatioXY; // dummy of tween factor
    var tweenCurvePoints = calculateTweenCurve(intersectionPoints1, intersectionPoints2, t); // tween curve

    measuredCircumference.push(secondBottomManualCircumference)
  
    const compressedManualCircumference = secondBottomManualCircumference * compression[2]/100;

    var offset = compressedManualCircumference / calculateCurveLength(new THREE.CatmullRomCurve3(tweenCurvePoints));
    //var offset = manualCircumference[i] / calculateCurveLength(new THREE.CatmullRomCurve3(tweenCurvePoints));
    tweenCurvePoints = offsetCurve(new THREE.CatmullRomCurve3(tweenCurvePoints), offset, centroidBaseshape);
    
    center = new THREE.Vector3(0, -interval*(nCrossSection-1) ,0);

    tweenCurvePoints = tweenCurvePoints.map((point) => new THREE.Vector3(point.x-centroidBaseshape.x, point.y-centroidBaseshape.y+center.y, point.z-centroidBaseshape.z));

    // flexion and adduction
    tweenCurvePoints = tweenCurvePoints.map((point) => {
      var newPoint = new THREE.Vector3(point.x, point.y, point.z);
      newPoint.applyAxisAngle(new THREE.Vector3(1, 0, 0), -initialFlexionAngle*Math.PI/180);
      newPoint.applyAxisAngle(new THREE.Vector3(0, 0, 1), -initialAdductionAngle*Math.PI/180);
      newPoint = newPoint.add(centroidBaseshape);
      return newPoint;
    });

    // tweenCurvePoints = new THREE.CatmullRomCurve3(tweenCurvePoints).getSpacedPoints(36);
    // tweenCurvePoints = new THREE.CatmullRomCurve3(tweenCurvePoints).getSpacedPoints(37);
    tweenCurvePoints = calculateIntersectionPoints(new THREE.CatmullRomCurve3(tweenCurvePoints), angleStep, close=true);

    objects.push(<TubeCurve
      key={key} 
      vpoints={tweenCurvePoints} 
      radius={1} 
      color={ (line_no===2) ? colorHigh : color }
      //rotation={new THREE.Vector3(-initialFlexionAngle*Math.PI/180, 0, -initialAdductionAngle*Math.PI/180)}
      //center={centroidBaseshape}
    />)
    key += 1;
  }
  

  // base shape
  measuredCircumference.push(measuredCircumferenceLength);
  
  if (activeStep === 3) { // compression
    objects.push(<TubeCurve
      key={key} 
      vpoints={basePoints} 
      radius={1} 
      color={color}
    />)
    key += 1;
  } else { // trim inside wall
    ///////////////////// medial trim function ///////////////////////
    const areAllElementsVector3 = medialTrimPoints.every(
      (element) => element instanceof THREE.Vector3
    );
    if (areAllElementsVector3 === false) {
      medialTrimPoints = medialTrimPoints.map((point) => new THREE.Vector3(point.x, point.y, point.z));
    }
    // change control point from key points to spaced points on curve
    basePoints = new THREE.CatmullRomCurve3(basePoints).getSpacedPoints(100);

    const intersections = calcIntersectionMedialCurves(new THREE.CatmullRomCurve3(basePoints), new THREE.CatmullRomCurve3(medialTrimPoints));
    console.assert(intersections.length === 2, "intersections.length should be 2");
    const closestPoint0 = findClosestPointOnPoints(basePoints, intersections[0]); // z < 0
    const closestPoint1 = findClosestPointOnPoints(basePoints, intersections[1]); // z > 0

    var maxIdx = Math.max(closestPoint0.idx, closestPoint1.idx);
    var minIdx = Math.min(closestPoint0.idx, closestPoint1.idx);
    console.assert(maxIdx - minIdx > basePoints.length/2, "maxIdx - minIdx should be greater than basePoints.length/2")

    const normal = new THREE.Vector3(0, 0, 1);
    if (maxIdx - minIdx > basePoints.length/2) { // this is weak condition, could caouse error
      for (let i = maxIdx; i < basePoints.length; i++) {
        const plane = new THREE.Plane(normal, -basePoints[i].z);
        //var tempPoint = findClosestPointOnPoints(new THREE.CatmullRomCurve3(medialTrimPoints).getPoints(50), basePoints[i]);
        const intersectionPoint = intersectionCurvePlane(new THREE.CatmullRomCurve3(medialTrimPoints), plane, 50, close=false);
        console.assert(intersectionPoint.length === 1, "intersectionPoint.length should be 1");

        basePoints[i] = new THREE.Vector3(basePoints[i].x, intersectionPoint[0].y, basePoints[i].z);
      }
      for (let i = 0; i <= minIdx; i++) {
        const plane = new THREE.Plane(normal, -basePoints[i].z);
        //var tempPoint = findClosestPointOnPoints(new THREE.CatmullRomCurve3(medialTrimPoints).getPoints(100), basePoints[i]);
        const intersectionPoint = intersectionCurvePlane(new THREE.CatmullRomCurve3(medialTrimPoints), plane, 50, close=false);
        console.assert(intersectionPoint.length === 1, "intersectionPoint.length should be 1");

        basePoints[i] = new THREE.Vector3(basePoints[i].x, intersectionPoint[0].y, basePoints[i].z);
      }
    } else {
      for (let i = minIdx; i <= maxIdx; i++) {
        const plane = new THREE.Plane(normal, -basePoints[i].z);
        //var tempPoint = findClosestPointOnPoints(new THREE.CatmullRomCurve3(medialTrimPoints).getPoints(100), basePoints[i]);
        const intersectionPoint = intersectionCurvePlane(new THREE.CatmullRomCurve3(medialTrimPoints), plane, 50, close=false);
        console.assert(intersectionPoint.length === 1, "intersectionPoint.length should be 1");

        basePoints[i] = new THREE.Vector3(basePoints[i].x, intersectionPoint[0].y, basePoints[i].z);
      }
    }
    
    //basePoints = remapCurve(new THREE.CatmullRomCurve3(basePoints), 2);

    objects.push(<TubeCurve
      key={key}
      vpoints={basePoints}
      radius={1}
      color={color}
    />)
    key += 1;
    
    if ( activeStep === 4 ) {
      objectsSub.push(<TubeCurve
        key={key}
        vpoints={medialTrimPoints}
        radius={1}
        color={colorSub}
      />)
      key += 1;
    }

    // medialTrimPoints.map((point) => {
    //   console.log(point)
    //   objectsSub.push(
    //     <Point>
    //       vpoints={point}
    //       color={colorHigh}
    //       size={26}
    //       key={key}
    //     </Point>
    //   )
    //   key += 1;
    // });
    objectsSub.push(<Points
      vpoints={medialTrimPoints}
      color={colorSub}
      size={3}
      key={key}
    />)

    // add point text
    medialTrimPoints.map((point, i) => {
      objectsSub.push(
        <Text
          key={key}
          position={[point.x, point.y, point.z]}
          rotation={[0, -Math.PI/2, 0]}
          color='white'
          fontSize={12}
          maxWidth={400} // option
          textAlign='left' // option
          anchorX='left' // option
          anchorY='middle' // option
        >
          P{i}
        </Text>
      )
      key += 1;
    });
  }

  const arrayObjectPoints = objects.map((object) => object.props.vpoints);
  for (let i = 0; i < arrayObjectPoints.length; i++) {
    callback({position: JSON.parse(JSON.stringify(arrayObjectPoints[i])), key: 'points', idx: i, maxLength: arrayObjectPoints.length});
  }

  /////// Sub objects, like text, etc /////////////////////////////////////////
  // warp curve
  var angleDiv = segmentsWidth;
  const warpPointsAll = [];
  for (let i = 0; i < nCrossSection+2; i++) {
    var warpPoints = calculateIntersectionPoints(new THREE.CatmullRomCurve3(objects[i].props.vpoints), 360/angleDiv, close=false);
    warpPointsAll.push(warpPoints);
  }
  for (let i = 0; i < angleDiv; i++) {
    if (i%10!==0) {
      continue
    }
    const warpPoints = warpPointsAll.map((points) => points[i]);
    objectsSub.push(<TubeCurve
      key={key} 
      vpoints={warpPoints} 
      radius={1} 
      color={color}
    />)
    key += 1;
  }


  /*
  ///////// Bottom surface /////////////////////////////////////////////////
  if (objects.length !== 0 && true ) {
    const bottomSurface = points2surface(oc, arrayObjectPoints[0]);

    if (bottomSurface) {
      console.log(bottomSurface)
      let geometries = visualize(oc, bottomSurface)
      geometries.forEach(geometry => {
        const material = new THREE.MeshBasicMaterial({ color: color, transparent: true, opacity: 0.4, side: THREE.DoubleSide });
        const mesh = new THREE.Mesh(geometry, material);
        objectsBottom.push(
          <primitive key={key} object={mesh} />
        );
        key += 1;
      });
    }
  }
  */
  
  const div = 30; 

  const divWarpPointsAll = [];
  const curveWarpAll = [];
  for (let i = 0; i < angleDiv; i++) {
    const warpPoints = warpPointsAll.map((points) => points[i]);
    const curveWarp = new THREE.CatmullRomCurve3(warpPoints);
    curveWarpAll.push(curveWarp);
    const curveWarpPoints = curveWarp.getSpacedPoints(div);
    divWarpPointsAll.push(curveWarpPoints)
  }

  // weft curve points from warp curve points
  const divWeftPointsAll = [];
  for (let i = 0; i < div+1; i++) {
    const divWeftPoints = divWarpPointsAll.map((points) => points[i]);
    divWeftPointsAll.push(divWeftPoints);
  }
  
  ///////// Complecated loft with opencascade.js /////////////////////////////////////////////////
  /*
  if (objects.length !== 0 && true ) {
    const arrayObjectPoints = objects.map((object) => object.props.vpoints);
    const loftPoints0 = makeCurveClockwise(arrayObjectPoints[0]);
    const loftPoints1 = alignSeamPoints(loftPoints0, makeCurveClockwise(arrayObjectPoints[1]));
    
    if (loftSurface) {
      console.log(loftSurface)
      let geometries = visualize(oc, loftSurface)
      geometries.forEach(geometry => {
        const material = new THREE.MeshBasicMaterial({ color: color, transparent: true, opacity: 0.4, side: THREE.DoubleSide });
        const mesh = new THREE.Mesh(geometry, material);
        objectsSurface.push(
          <primitive key={key} object={mesh} />
        );
        key += 1;
      });
    }
  }
  */

  ///////// Simple loft with three.js /////////////////////////////////////////////////
  if (objects.length !== 0) {
    //var geom = BufferGeometryUtils();
    for (let i = 0; i < divWeftPointsAll.length-1; i++) {
      const loftPoints0 = makeCurveClockwise(divWeftPointsAll[i]);
      const loftPoints1 = alignSeamPoints(loftPoints0, makeCurveClockwise(divWeftPointsAll[i+1]));
      const loftCurve0 = new THREE.CatmullRomCurve3(loftPoints0, true);
      const loftCurve1 = new THREE.CatmullRomCurve3(loftPoints1, true);

      const startPercent = i*1/(divWeftPointsAll.length-1);
      const endPercent = (i+1)*1/(divWeftPointsAll.length-1);
      
      const segmentPoints = [];
      const numberOfPoints = segmentsHeight;
      const segmentPointsAll = [];

      // for (let j = 0; j < curveWarpAll.length; j++) {
      //   for (let k = 0; k <= numberOfPoints; k++) {
      //     const t = startPercent + (endPercent - startPercent) * (k / numberOfPoints);
      //     //console.log(t)
      //     //console.log(curveWarpAll[j])
      //     segmentPoints.push(curveWarpAll[j].getPointAt(t));
      //   }

      //   //const segmentWrapCurve = new THREE.CatmullRomCurve3(segmentPoints);
      //   segmentPointsAll.push(segmentPoints);
      // }

      // const loftSurface = createLoftSurfaceFrom2CurvesSmooth(loftCurve0, loftCurve1, segmentPointsAll, segmentsWidth, segmentsHeight, color);
      const loftSurface = createLoftSurfaceFrom2Curves(loftCurve0, loftCurve1, segmentsWidth, segmentsHeight, color);

      
      /*
      // bottom surface
      if (i===0) {
        
        const centroid = new THREE.Vector3();
        loftPoints0.reverse(); // for reverse nor
        loftPoints0.forEach((vector) => {
          centroid.add(vector);
        });
        centroid.divideScalar(loftPoints0.length);
        loftPoints0.unshift(centroid);

        
        const bottomGeometry = new THREE.BufferGeometry();
        const loftPoints0Raw = []
        loftPoints0.map((point) => loftPoints0Raw.push(point.x, point.y, point.z));
        bottomGeometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(loftPoints0Raw), 3));
        const indices = [];

        for (let i = 1; i < loftPoints0.length-1; i++) {
          indices.push(0, i, i + 1);
        }
        indices.push(0, loftPoints0.length-1, 1); // 最後の三角形を追加
        bottomGeometry.setIndex(indices);
        bottomGeometry.computeVertexNormals();
        // 空のuv属性を追加, 後で追加するparametric geometryと同じ属性を持つ必要がある
        const vertexCount = bottomGeometry.getAttribute('position').count;
        const emptyUVs = new Float32Array(vertexCount * 2); // 各頂点について2つの要素（u、v）が必要
        const uvAttribute = new THREE.BufferAttribute(emptyUVs, 2);
        bottomGeometry.setAttribute('uv', uvAttribute);

        const material = new THREE.MeshLambertMaterial({color: color, transparent: true, opacity: 0.4, side: THREE.DoubleSide});
        const mesh = new THREE.Mesh(bottomGeometry, material);
        objectsSurface.push(
          <primitive key={key} object={mesh} />
        );
        key += 1;
      }
      */

      objectsSurface.push(
        <primitive key={key} object={loftSurface} />
      );
      key += 1;
    }

    if (objectsSurface.length!==0) {
      var geomObject = objectsSurface.map((obj) => {
        return obj.props.object.geometry;
      })
      var geom = BufferGeometryUtils.mergeGeometries(geomObject);
      callback2(geom);
    }
  }

  if (activeStep === 7) {
    objectsSurface = [];
  }

  return objects.length !== 0 ? (
    <group ref={refGroup}>
      {objects}
      {objectsSub}
      {objectsSurface}
    </group>
  ) : null;
})

/**
 * Calculate cross sections
 */
export const CrossSectionsAkScan = memo(({
  activeStep,
  oriMeshLeg,
  stumpLength,
  basePoints,
  centroidBaseshape,
  interval,
  smoothness,
  bottomExtensionCurveRadius,
  bottomExtension,
  initialAdductionAngle,
  initialFlexionAngle,
  line_no,
  compression,
  manualCircumference,
  manualOrScan,
  tweenFactor,
  p0Y,
  p0Z,
  p1Y,
  p1Z,
  p2Y,
  p2Z,
  p3Y,
  p3Z,
  segments=36,
  color,
  colorHigh,
  colorSub,
  callback,
  callback2,
  callback3
}) => {
  const objects = [];
  const objectsSub = [];
  const objectsSurface = [];
  
  const contourPointsAll = [];
  const oriContourPointsAll = [];

  var key = 0;
  var ref = useRef();
  const nBasePoints = 50;
  const segmentsWidth = 100;
  const segmentsHeight = 10;

  console.log("CrossSections");
  if (!oriMeshLeg) return;

  objects.length = 0; // clear array
  objectsSub.length = 0; // clear array
  objectsSurface.length = 0; // clear array

  // calc stump length from bounding box of the leg mesh
  const meshLeg = oriMeshLeg.clone();
  meshLeg.geometry.computeBoundingBox();
  const bbox = meshLeg.geometry.boundingBox;

  const nCrossSection = Math.ceil((stumpLength) / interval) + 1
  const offsets = [];

  if (activeStep !== 3) {
    colorHigh = color;
  }

  const medialTrimPoints = [
    new THREE.Vector3(0, p0Y, p0Z),
    new THREE.Vector3(0, p1Y, p1Z),
    new THREE.Vector3(0, p2Y, p2Z),
    new THREE.Vector3(0, p3Y, p3Z),
  ]

  console.log('nCross: ', nCrossSection)
  
  // bottom extension curve /////////////////////////////////////////////////
  // let center = new THREE.Vector3(0, -stumpLength-bottomExtension, 20) // rotation center and object center is defined as the below centroidBaseshape
  // let bottomExtensionPoints = calcCirclePoints(center, 30, segments);

  // contourPointsAll.push(bottomExtensionPoints);


  // get contour points
  for (let i = 0; i < nCrossSection; i++) {
    let offset = 0
    if (i == 0 || i == 1) {
      offset = -stumpLength + 20; // 20 mm is the offset to get circumference from the bottom
    } else {
      offset = -(nCrossSection - i) * interval;
    }
    
    offsets.push(offset);

    let plane = new THREE.Plane(new THREE.Vector3(0,1,0), -offset);
    let vertices = drawIntersectionPoints(meshLeg, plane, offset, false)
    
    console.log(i)
    console.log(vertices.length)
    if (vertices.length == 0) continue

    let contourPoints = sortPoints(vertices);
    const seamPoint = [new THREE.Vector3(0, 0, 1000)];
    contourPoints = alignSeamPoints(seamPoint, contourPoints);
    contourPoints = new THREE.CatmullRomCurve3(contourPoints, true).getSpacedPoints(nBasePoints);
    contourPoints = sortPoints(contourPoints); // make sure the contour points are sorted
    contourPoints = new THREE.CatmullRomCurve3(contourPoints, true).getSpacedPoints(nBasePoints);
    contourPoints = makeCurveClockwise(contourPoints);

    const oriCurveLength = new THREE.CatmullRomCurve3(contourPoints).getLength();
    let oriContourPoints = contourPoints.map((point) => {
      return point.clone();
    });
    //////// Smooth //////////////////////////
    // Smooth value is 1 to 10. Smooth func reduce the number of points of the curve by 1/smoothness. 
    // Then return to the original number of points by using CatmullRomCurve3
    contourPoints = new THREE.CatmullRomCurve3(contourPoints, false).getSpacedPoints(Math.floor(contourPoints.length/smoothness[i]));
    
    ////// Compression ////////////////////////////
    // remove last point because the last point is the same as the first point of the curve due to the closed curve
    contourPoints.pop();
    contourPoints = new THREE.CatmullRomCurve3(contourPoints, true).getSpacedPoints(nBasePoints);

    const curveLength = new THREE.CatmullRomCurve3(contourPoints).getLength();
    const compressedCircumference = curveLength * compression[i] / 100;
    let offsetCircumference = 0
    
    if (((i == nCrossSection - 1) || (i == nCrossSection - 2)) && manualOrScan[i]) { // manual measurement based
      const temp = calculateEllipseRadius(manualCircumference[i], 1)
      const rx = temp.rx;
      const ry = temp.ry;
      // contourPoints = new THREE.EllipseCurve(0, 0, rx, ry, 0, 2*Math.PI, true).getPoints(100);
      let contourPointsEllipse = new THREE.EllipseCurve(0, 0, rx, ry, 0, 2*Math.PI, true).getPoints(100); // x.u
      

      if (tweenFactor[i]) {
        const angleStep = 10;
        const centerScan = calcCentroid(contourPoints);
        console.log(centerScan)
        // convert x,y to x,y,z
        contourPointsEllipse = contourPointsEllipse.map((point) => new THREE.Vector3(point.x, 0, point.y));
        // substract centroidBaseshape from contourPointsEllipse
        const basePointsToCenter = basePoints.map((point) => point.clone().sub(centroidBaseshape));

        // Calculate tween curve
        const intersectionPoints1 = calculateIntersectionPoints(new THREE.CatmullRomCurve3(basePointsToCenter), angleStep, close=true); // base curve for tween
        const intersectionPoints2 = calculateIntersectionPoints(new THREE.CatmullRomCurve3(contourPointsEllipse), angleStep, close=true); // base curve for tween
        let tweenCurvePoints = calculateTweenCurve(intersectionPoints1, intersectionPoints2, tweenFactor[i]); // tween curve
        // adjust height
        contourPoints = tweenCurvePoints.map((point) => new THREE.Vector3(point.x, offset, point.z));
        // smooth
        contourPoints = new THREE.CatmullRomCurve3(contourPoints, false).getSpacedPoints(Math.floor(contourPoints.length/smoothness[i]));

        // rescall
        const curveLength = new THREE.CatmullRomCurve3(contourPoints).getLength();
        offsetCircumference = manualCircumference[i] / curveLength * compression[i] / 100;
        contourPoints = offsetCurve(new THREE.CatmullRomCurve3(contourPoints), offsetCircumference, calcCentroid(contourPoints));
        
        // adjust center
        //const diff = centroidBaseshape.clone().sub(centerScan);
        //contourPoints = contourPoints.map((point) => point.clone().add(new THREE.Vector3(diff.x, 0, diff.z)));

      } else {
        // adjust height
        contourPoints = contourPointsEllipse.map((point) => new THREE.Vector3(point.x, offset, point.y));
      }
    } else { // scan based
      // rescall
      offsetCircumference = compressedCircumference / curveLength * oriCurveLength / curveLength;
      contourPoints = offsetCurve(new THREE.CatmullRomCurve3(contourPoints), offsetCircumference, calcCentroid(contourPoints));
    }

    ///// bottom extension /////
    if (i == 0) { // bottom curve
      const bottomExtensionPoints = [];
      const curve = new THREE.CatmullRomCurve3(contourPoints);
      const offsetContourPoints = offsetCurve(curve, 0.5);
      for (let j=0; j<offsetContourPoints.length; j++) {
        const point = offsetContourPoints[j];
        const offset = -20 - bottomExtension;
        bottomExtensionPoints.push(new THREE.Vector3(point.x, point.y+offset, point.z));
      }
      contourPoints = bottomExtensionPoints;
      oriContourPoints = bottomExtensionPoints;
    } else if (i == 1) {
      contourPoints = contourPoints.map((point) => {
        return new THREE.Vector3(point.x, point.y-bottomExtension, point.z);
      });
    }
    
    // apply flexion and adduction angle
    const centerCountour = calcCentroid(contourPoints);
    contourPoints = contourPoints.map((point) => {
      let newPoint = new THREE.Vector3(point.x, point.y, point.z);
      newPoint = newPoint.add(centroidBaseshape);
      newPoint = newPoint.sub(new THREE.Vector3(centerCountour.x, 0, centerCountour.z));
      newPoint.applyAxisAngle(new THREE.Vector3(1, 0, 0), -initialFlexionAngle*Math.PI/180);
      newPoint.applyAxisAngle(new THREE.Vector3(0, 0, 1), -initialAdductionAngle*Math.PI/180);
      return newPoint;
    });

    contourPointsAll.push(contourPoints);
    oriContourPointsAll.push(oriContourPoints);
  }

  ///// top shape /////
  if (activeStep !== 3) { // except compression phase
    ///// medial trim function /////
    basePoints = trimMedialPoints(basePoints, medialTrimPoints);

    if ( activeStep === 4 ) {
      objectsSub.push(<TubeCurve
        key={key}
        vpoints={medialTrimPoints}
        radius={1}
        color={colorSub}
      />)
      key += 1;
    }
  
    objectsSub.push(<Points
      vpoints={medialTrimPoints}
      color={colorSub}
      size={3}
      key={key}
    />)
  
    // add point text
    medialTrimPoints.map((point, i) => {
      objectsSub.push(
        <Text
          key={key}
          position={[point.x, point.y, point.z]}
          rotation={[0, -Math.PI/2, 0]}
          color='white'
          fontSize={12}
          maxWidth={400} // option
          textAlign='left' // option
          anchorX='left' // option
          anchorY='middle' // option
        >
          P{i}
        </Text>
      )
      key += 1;
    });
  }

  contourPointsAll.push(basePoints);
  oriContourPointsAll.push(basePoints);
  callback3(oriContourPointsAll)

  for (let i = 0; i < contourPointsAll.length; i++) {
    objects.push(<TubeCurve 
      key={key} 
      vpoints={contourPointsAll[i]} 
      radius={1}
      color={ (line_no===i) ? colorHigh : color }
      />
    );
    key += 1;
  }


  ///// get meidan points /////
  const medianPointsAll = [];
  for (let angle = 0; angle < 360; angle += 90) {
    const radians = THREE.MathUtils.degToRad(angle);
    const normal = new THREE.Vector3(Math.cos(radians), 0, Math.sin(radians));
    
    const medianPoints = [];
    for (let i = 0; i < contourPointsAll.length; i++) {
      const curve = new THREE.CatmullRomCurve3(contourPointsAll[i]);
      const center = calculateCurveCenter(curve);
      const plane = new THREE.Plane(normal, -normal.dot(center));

      const intersectionPoints = intersectionCurvePlane(curve, plane, 300);
      if (intersectionPoints.length == 0) continue;

      var refPoint = normal.clone().multiplyScalar(50);
      // rotation matrix to rotate around y axis to make the normal vector of the plane to be (0, 0, 1)
      var rotationMatrix = new THREE.Matrix4().makeRotationAxis(new THREE.Vector3(0, 1, 0), Math.PI / 2);
      // apply rotation
      refPoint.applyMatrix4(rotationMatrix);
      refPoint = refPoint.add(center);

      const closestPoint = findClosestPointOnPoints(intersectionPoints, refPoint);
      
      medianPoints.push(closestPoint.point);
    }
    medianPointsAll.push(medianPoints);
  }

  medianPointsAll.map((points) => {
    objectsSub.push(<TubeCurve
      key={key}
      vpoints={points}
      radius={0.5}
      color={color}
    />);
    key += 1;
  });

  // save cross section points
  const arrayObjectPoints = objects.map((object) => object.props.vpoints);
  for (let i = 0; i < arrayObjectPoints.length; i++) {
    callback({position: JSON.parse(JSON.stringify(arrayObjectPoints[i])), key: 'points', idx: i, maxLength: arrayObjectPoints.length});
  }

  /////// Sub objects, like text, etc /////////////////////////////////////////
  // warp curve
  var angleDiv = segmentsWidth;
  const warpPointsAll = [];
  for (let i = 0; i < nCrossSection+1; i++) {
    var warpPoints = calculateIntersectionPoints(new THREE.CatmullRomCurve3(objects[i].props.vpoints), 360/angleDiv, close=false);
    warpPointsAll.push(warpPoints);
  }
  for (let i = 0; i < angleDiv; i++) {
    if (i%10 !== 0) {
      continue
    }
    const warpPoints = warpPointsAll.map((points) => points[i]);
    objectsSub.push(<TubeCurve
      key={key} 
      vpoints={warpPoints} 
      radius={0.5}
      color={color}
    />)
    key += 1;
  }

  if (objects.length !== 0) {
    const loftSurfaces = weftPoints2surface(objects, warpPointsAll, angleDiv, segmentsWidth, segmentsHeight, color);
    
    loftSurfaces.map((surface) => {
      objectsSurface.push(
        <primitive key={key} object={surface} />
      );
      key += 1;
    })

    if (objectsSurface.length!==0) {
      var geomObject = objectsSurface.map((obj) => {
        return obj.props.object.geometry;
      })
      var geom = BufferGeometryUtils.mergeGeometries(geomObject);
      callback2(geom);
    }
  }

  if (activeStep === 7) {
    objectsSurface = [];
  }

  return (
    (objectsSub.length > 0) ? (
      <group>
        {objects}
        {objectsSub}
        {objectsSurface}
      </group>
    ) : null
  )
});